<template>
  <div>
    <!-- Row: Template / Unit -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("risk.template.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="templateFilter"
          :options="templateOptions"
          :disabled="isTemplateFilterDisabled"
          class="w-100"
          label="name"
          :reduce="(val) => val.id"
          @input="(val) => $emit('update:templateFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("unit.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="unitFilter"
          :options="unitOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:unitFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="showBauExtraFilters"
      align-v="center"
      class="d-flex align-items-center"
    >
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("planning.business_division") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="sectorFilter"
          :options="sectorSelectOptions"
          class="w-100"
          label="label"
          :reduce="reduceOptionToValue"
          @input="(val) => $emit('update:sectorFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $t("planning.federal_entity") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="regionFilter"
          :options="regionSelectOptions"
          class="w-100"
          label="label"
          :reduce="reduceOptionToValue"
          @input="(val) => $emit('update:regionFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>

    <!-- Row: User / Status -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("users.title") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="userFilter"
          :options="userOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:userFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("status") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="statusFilter"
          :options="statusOptions"
          class="w-100"
          label="label"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:statusFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>
    <!-- Row: From / To -->
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("safr.from") }}</label>
        <flat-pickr
          class="form-control"
          :config="dateTimeConfig"
          :value="fromFilter"
          @input="$emit('update:fromFilter', $event)"
          @on-change="(selectedDates, val) => $emit('update:fromFilter', val)"
        />
      </b-col>
      <b-col cols="6" class="mb-2">
        <label class="text-capitalize">{{ $tc("safr.to") }}</label>
        <flat-pickr
          class="form-control"
          :config="dateTimeConfig"
          :value="toFilter"
          @input="$emit('update:toFilter', $event)"
          @on-change="(selectedDates, val) => $emit('update:toFilter', val)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import { BRow, BCol, BFormInput } from "bootstrap-vue";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import flatPickr from "vue-flatpickr-component";
import store from "@/store";

export default {
  name: "FiltersSecuritySelfAudits",
  components: {
    BRow,
    BCol,
    BFormInput,
    vSelect,
    flatPickr,
  },
  props: {
    templateOptions: {
      type: Array,
      default: () => []
    },
    templateFilter: {
      type: Number,
      default: null
    },
    isTemplateFilterDisabled: {
      type: Boolean,
      default: false
    },
    showBauExtraFilters: {
      type: Boolean,
      default: false,
    },
    unitOptions: {
      type: Array,
      default: () => []
    },
    unitFilter: {
      type: [Number, String],
      default: null
    },
    sectorFilter: {
      type: [Number, String],
      default: null,
    },
    regionFilter: {
      type: [Number, String],
      default: null,
    },
    userOptions: {
      type: Array,
      default: () => []
    },
    userFilter: {
      type: [Number, String],
      default: null
    },
    statusOptions: {
      type: Array,
      default: () => []
    },
    statusFilter: {
      type: [Number, String],
      default: null
    },
    fromFilter: {
      type: String,
      default: null
    },
    toFilter: {
      type: String,
      default: null
    }
  },
  setup() {
    const enableTimeInputEditing = (...hookArgs) => {
      const instance = hookArgs[2];

      [instance.hourElement, instance.minuteElement, instance.secondElement]
        .filter(Boolean)
        .forEach((input) => {
          input.step = "1";
          input.tabIndex = 0;
          input.readOnly = false;
          input.disabled = false;
        });
    };

    const dateTimeConfig = {
      allowInput: true,
      disableMobile: true,
      enableSeconds: true,
      enableTime: true,
      dateFormat: "Y-m-d H:i:S",
      hourIncrement: 1,
      minuteIncrement: 1,
      onOpen: enableTimeInputEditing,
      onReady: enableTimeInputEditing,
      time_24hr: true,
    };

    const reduceOptionToValue = (item) => {
      if (item == null) return item;
      return item.value != null ? item.value : item.id;
    };

    const sectorSelectOptions = computed(() => {
      const raw = store.getters["sector/getSelectedItems"] || [];
      return raw.filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label:
          item.label != null
            ? item.label
            : item.name != null
              ? item.name
              : String(item.value != null ? item.value : item.id),
      }));
    });

    const regionSelectOptions = computed(() => {
      const raw = store.getters["region/getSelectedItems"] || [];
      return raw.filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label:
          item.label != null
            ? item.label
            : item.name != null
              ? item.name
              : String(item.value != null ? item.value : item.id),
      }));
    });

    onMounted(() => {
      store.dispatch("sector/getAll");
      store.dispatch("region/getAll");
    });

    return {
      withPopper: popperHelper.withPopper,
      dateTimeConfig,
      reduceOptionToValue,
      sectorSelectOptions,
      regionSelectOptions,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.errorText {
  color: red;
  font-size: 12px;
}
</style>
